import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../supabase/client';

interface Job {
  id: string;
  company_name: string;
  campaign_name: string;
  product_context: string;
  icp_hypothesis: string;
  status: string;
  total_records: number;
  classified_count: number;
  review_count: number;
  created_at: string;
}

interface Classification {
  id: string;
  lead_n: number;
  first_name: string | null;
  last_name: string | null;
  linkedin_url: string | null;
  title: string | null;
  company: string | null;
  reply_text: string | null;
  intent_tier_raw: string | null;
  icp_fit: string | null;
  intent_tier_llm: string | null;
  is_icp_override: boolean;
  reasoning: string | null;
}

interface Review {
  classification_id: string;
  human_tier: string;
  human_icp_fit: string;
  notes: string | null;
}

interface Metrics {
  total:              number;
  has_reply:          number;
  tier_a:             number;
  tier_b:             number;
  tier_c:             number;
  tier_d:             number;
  tier_e:             number;
  in_icp:             number;
  off_icp:            number;
  substantive:        number;      // replied - E
  substantive_excl_d: number;      // replied - E - D
  a_rate:             number;      // A / substantive_excl_d
  contamination_rate: number;      // D / substantive
  override_count:     number;
  override_confirmed_in_icp: number;
}

function computeMetrics(records: Classification[], reviews: Review[]): Metrics {
  const reviewMap = new Map(reviews.map(r => [r.classification_id, r]));

  // Effective tier: use human review if available, else model output
  function effectiveTier(r: Classification): string {
    return reviewMap.get(r.id)?.human_tier ?? r.intent_tier_llm ?? 'E';
  }
  function effectiveICP(r: Classification): string {
    return reviewMap.get(r.id)?.human_icp_fit ?? r.icp_fit ?? 'unknown';
  }

  const classified = records.filter(r => r.intent_tier_llm !== null);
  const has_reply  = classified.filter(r => (r.reply_text ?? '').trim() !== '');

  const tiers = { A: 0, B: 0, C: 0, D: 0, E: 0 };
  let in_icp = 0, off_icp = 0;

  for (const r of classified) {
    const t = effectiveTier(r);
    if (t in tiers) tiers[t as keyof typeof tiers]++;
    const ic = effectiveICP(r);
    if (ic === 'in_icp')  in_icp++;
    if (ic === 'off_icp') off_icp++;
  }

  const substantive        = has_reply.length - tiers.E;
  const substantive_excl_d = Math.max(0, substantive - tiers.D);
  const a_rate             = substantive_excl_d > 0 ? tiers.A / substantive_excl_d : 0;
  const contamination_rate = substantive > 0 ? tiers.D / substantive : 0;

  const overrides = records.filter(r => r.is_icp_override);
  const override_confirmed_in_icp = overrides.filter(r =>
    reviewMap.get(r.id)?.human_icp_fit === 'in_icp'
  ).length;

  return {
    total:              records.length,
    has_reply:          has_reply.length,
    tier_a:             tiers.A,
    tier_b:             tiers.B,
    tier_c:             tiers.C,
    tier_d:             tiers.D,
    tier_e:             tiers.E,
    in_icp,
    off_icp,
    substantive,
    substantive_excl_d,
    a_rate,
    contamination_rate,
    override_count:     overrides.length,
    override_confirmed_in_icp,
  };
}

function computeVerdict(m: Metrics): { verdict: 'GREEN' | 'YELLOW' | 'RED'; reasons: string[] } {
  const reasons: string[] = [];

  if (m.total < 50) {
    reasons.push('Volume below 50 — insufficient sample size. Collect more responses before drawing conclusions.');
    return { verdict: 'YELLOW', reasons };
  }

  const aRatePct = Math.round(m.a_rate * 1000) / 10;
  const contamPct = Math.round(m.contamination_rate * 1000) / 10;

  if (m.a_rate >= 0.04 && m.contamination_rate <= 0.40) {
    reasons.push(`A-rate ${aRatePct}% ≥ 4% threshold — strong concrete-interest signal.`);
    reasons.push(`Contamination rate ${contamPct}% ≤ 40% — ICP targeting is clean.`);
    return { verdict: 'GREEN', reasons };
  }

  if (m.a_rate >= 0.02) {
    reasons.push(`A-rate ${aRatePct}% ≥ 2% — positive signal, but below GREEN threshold (4%).`);
    if (m.contamination_rate > 0.40) {
      reasons.push(`Contamination rate ${contamPct}% > 40% — ICP targeting may need refinement.`);
    }
    return { verdict: 'YELLOW', reasons };
  }

  reasons.push(`A-rate ${aRatePct}% < 2% — weak concrete-interest signal.`);
  if (m.contamination_rate > 0.40) {
    reasons.push(`Contamination rate ${contamPct}% > 40% — significant off-ICP volume in responses.`);
  }
  return { verdict: 'RED', reasons };
}

const VERDICT_STYLE = {
  GREEN:  { bg: 'bg-green-50',  border: 'border-green-300', text: 'text-green-800',  emoji: '🟢' },
  YELLOW: { bg: 'bg-amber-50',  border: 'border-amber-300', text: 'text-amber-800',  emoji: '🟡' },
  RED:    { bg: 'bg-red-50',    border: 'border-red-300',   text: 'text-red-800',    emoji: '🔴' },
};

const TIER_COLOR: Record<string, string> = {
  A: 'bg-green-100 text-green-800',
  B: 'bg-blue-100 text-blue-800',
  C: 'bg-yellow-100 text-yellow-800',
  D: 'bg-red-100 text-red-800',
  E: 'bg-gray-100 text-gray-600',
};

const CSRICPVerdict: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();

  const [job, setJob] = useState<Job | null>(null);
  const [records, setRecords] = useState<Classification[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAllRecords, setShowAllRecords] = useState(false);

  useEffect(() => {
    if (jobId) loadData();
  }, [jobId]);

  async function loadData() {
    setLoading(true);
    setError(null);

    const { data: jobData, error: jobErr } = await supabase
      .from('icp_jobs')
      .select('id, company_name, campaign_name, product_context, icp_hypothesis, status, total_records, classified_count, review_count, created_at')
      .eq('id', jobId)
      .single();
    if (jobErr || !jobData) { setError('Job not found'); setLoading(false); return; }
    setJob(jobData as Job);

    // Load all classifications
    const { data: recs, error: recErr } = await supabase
      .from('icp_classifications')
      .select('id, lead_n, first_name, last_name, linkedin_url, title, company, reply_text, intent_tier_raw, icp_fit, intent_tier_llm, is_icp_override, reasoning')
      .eq('job_id', jobId)
      .order('lead_n');
    if (recErr) { setError(recErr.message); setLoading(false); return; }

    const classRecs = (recs ?? []) as Classification[];
    setRecords(classRecs);

    // Load all reviews for these records
    const ids = classRecs.map(r => r.id);
    if (ids.length > 0) {
      const { data: revData } = await supabase
        .from('icp_reviews')
        .select('classification_id, human_tier, human_icp_fit, notes')
        .in('classification_id', ids);
      setReviews((revData ?? []) as Review[]);
    }

    setLoading(false);
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-400 text-center">
          <div className="text-3xl mb-2">⏳</div>
          <p>Computing verdict…</p>
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-red-600 text-center">
          <div className="text-3xl mb-2">⚠️</div>
          <p>{error ?? 'Job not found'}</p>
        </div>
      </div>
    );
  }

  const metrics = computeMetrics(records, reviews);
  const { verdict, reasons } = computeVerdict(metrics);
  const vStyle = VERDICT_STYLE[verdict];

  const aRatePct     = (metrics.a_rate * 100).toFixed(1);
  const contamPct    = (metrics.contamination_rate * 100).toFixed(1);
  const replyRatePct = metrics.total > 0
    ? ((metrics.has_reply / metrics.total) * 100).toFixed(1)
    : '0.0';

  // Records for the detail table
  const reviewMap = new Map(reviews.map(r => [r.classification_id, r]));
  const displayedRecords = showAllRecords
    ? records.filter(r => r.intent_tier_llm !== null)
    : records.filter(r => ['A', 'B'].includes(
        reviewMap.get(r.id)?.human_tier ?? r.intent_tier_llm ?? ''
      ));

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Nav */}
        <button
          onClick={() => navigate('/csr-icp-signal')}
          className="text-sm text-gray-500 hover:text-gray-700 mb-4 flex items-center gap-1"
        >
          ← All Analyses
        </button>

        {/* Title */}
        <div className="mb-6">
          <h1 className="text-xl font-bold text-gray-900">📊 ICP Signal Verdict</h1>
          <p className="text-sm text-gray-500 mt-1">
            <strong>{job.company_name}</strong> · {job.campaign_name}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">
            {job.classified_count} records classified · {new Date(job.created_at).toLocaleDateString()}
          </p>
        </div>

        {/* Verdict card */}
        <div className={`${vStyle.bg} border-2 ${vStyle.border} rounded-xl p-6 mb-6`}>
          <div className="flex items-center gap-3 mb-3">
            <span className="text-3xl">{vStyle.emoji}</span>
            <div>
              <div className={`text-xl font-bold ${vStyle.text}`}>{verdict}</div>
              <div className="text-xs text-gray-500 italic mt-0.5">All verdicts are provisional — thresholds are placeholders pending calibration data.</div>
            </div>
          </div>
          <ul className="space-y-1">
            {reasons.map((r, i) => (
              <li key={i} className={`text-sm ${vStyle.text}`}>• {r}</li>
            ))}
          </ul>
        </div>

        {/* Funnel stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total targeted',  value: metrics.total,          sub: '100%' },
            { label: 'Replied',         value: metrics.has_reply,       sub: `${replyRatePct}% response rate` },
            { label: 'Tier A (concrete)', value: metrics.tier_a,        sub: `${aRatePct}% A-rate` },
            { label: 'Contamination (D)', value: metrics.tier_d,        sub: `${contamPct}% of substantive` },
          ].map(({ label, value, sub }) => (
            <div key={label} className="bg-white rounded-xl border border-gray-200 p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{value}</div>
              <div className="text-xs font-medium text-gray-600 mt-0.5">{label}</div>
              <div className="text-xs text-gray-400 mt-0.5">{sub}</div>
            </div>
          ))}
        </div>

        {/* Tier breakdown */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">Intent tier breakdown</h2>
          <div className="flex gap-3 flex-wrap">
            {(['A', 'B', 'C', 'D', 'E'] as const).map(tier => {
              const count = metrics[`tier_${tier.toLowerCase()}` as keyof Metrics] as number;
              const pct   = metrics.has_reply > 0 ? ((count / metrics.has_reply) * 100).toFixed(0) : '0';
              return (
                <div key={tier} className="flex items-center gap-2">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${TIER_COLOR[tier]}`}>
                    {tier}
                  </span>
                  <span className="text-sm text-gray-700">{count}</span>
                  <span className="text-xs text-gray-400">({pct}%)</span>
                </div>
              );
            })}
          </div>
          <div className="mt-3 text-xs text-gray-400">
            Substantive replies (excl. E): <strong>{metrics.substantive}</strong> ·
            Substantive excl. D: <strong>{metrics.substantive_excl_d}</strong>
          </div>
        </div>

        {/* ICP override insight */}
        {metrics.override_count > 0 && (
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-5 mb-6">
            <h2 className="text-sm font-semibold text-purple-800 mb-2">🔎 ICP Override Insight</h2>
            <p className="text-sm text-purple-700">
              <strong>{metrics.override_count}</strong> leads showed genuine interest (Tier A or B) but
              the model classified them as off-ICP.
              {metrics.override_confirmed_in_icp > 0 && (
                <> After your review, <strong>{metrics.override_confirmed_in_icp}</strong> were confirmed
                as in-ICP — suggesting your stated ICP may be narrower than your actual buyers.</>
              )}
            </p>
            {metrics.override_confirmed_in_icp > 0 && (
              <p className="text-sm text-purple-600 mt-2 italic">
                Consider revising the ICP hypothesis to capture these behavioral signals before re-running.
              </p>
            )}
          </div>
        )}

        {/* Lead table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-4">
          <div className="px-5 py-3 border-b border-gray-100 flex justify-between items-center">
            <h2 className="text-sm font-semibold text-gray-700">
              {showAllRecords ? 'All classified records' : 'Tier A & B leads (strongest signals)'}
            </h2>
            <button
              onClick={() => setShowAllRecords(v => !v)}
              className="text-xs text-indigo-600 hover:underline"
            >
              {showAllRecords ? 'Show Tier A & B only' : 'Show all records'}
            </button>
          </div>
          {displayedRecords.length === 0 ? (
            <div className="px-5 py-8 text-center text-gray-400 text-sm">
              No Tier A or B records found.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Lead</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Reply</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tier</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">ICP</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {displayedRecords.map(rec => {
                    const rev = reviewMap.get(rec.id);
                    const tier = rev?.human_tier ?? rec.intent_tier_llm ?? '?';
                    const icp  = rev?.human_icp_fit ?? rec.icp_fit ?? 'unknown';
                    return (
                      <tr key={rec.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-gray-400 font-mono text-xs">{rec.lead_n}</td>
                        <td className="px-4 py-2">
                          <div className="font-medium text-gray-900 text-xs">
                            {[rec.first_name, rec.last_name].filter(Boolean).join(' ') || '—'}
                            {rec.is_icp_override && (
                              <span className="ml-1 text-xs text-amber-500">⚑</span>
                            )}
                          </div>
                          <div className="text-xs text-gray-400">
                            {rec.title || '—'} · {rec.company || '—'}
                          </div>
                          {rec.linkedin_url && (
                            <a
                              href={rec.linkedin_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-indigo-400 hover:underline"
                            >
                              LinkedIn ↗
                            </a>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <p className="text-xs text-gray-600 max-w-xs line-clamp-2">
                            {rec.reply_text?.trim() || <span className="italic text-gray-400">[empty]</span>}
                          </p>
                        </td>
                        <td className="px-4 py-2">
                          <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-bold ${TIER_COLOR[tier] ?? ''}`}>
                            {tier}
                          </span>
                          {rev && <span className="text-xs text-gray-400 ml-1">human</span>}
                        </td>
                        <td className="px-4 py-2 text-xs text-gray-600">
                          {icp === 'in_icp' ? '✓ In-ICP' : icp === 'off_icp' ? '✗ Off-ICP' : '?'}
                          {rev && <span className="text-xs text-gray-400 ml-1">human</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Context panel */}
        <details className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
          <summary className="text-sm font-medium text-gray-700 cursor-pointer">
            Campaign context used for classification
          </summary>
          <div className="mt-4 space-y-3 text-sm">
            <div>
              <div className="text-xs font-medium text-gray-500 uppercase mb-1">Product context</div>
              <p className="text-gray-700 whitespace-pre-wrap">{job.product_context}</p>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-500 uppercase mb-1">ICP hypothesis</div>
              <p className="text-gray-700 whitespace-pre-wrap">{job.icp_hypothesis}</p>
            </div>
          </div>
        </details>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={() => navigate(`/csr-icp-signal/${jobId}/review`)}
            className="text-gray-600 border border-gray-300 px-5 py-2 rounded-lg text-sm hover:bg-gray-50"
          >
            ← Back to Review Queue
          </button>
          <button
            onClick={() => navigate('/csr-icp-signal/new')}
            className="bg-indigo-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700"
          >
            + New Analysis
          </button>
        </div>
      </div>
    </div>
  );
};

export default CSRICPVerdict;
